import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tap, catchError, throwError } from 'rxjs';
import { ToastService } from '../services/toast.service';

/**
 * Helper to extract error message, backend error code, and field validation detail list
 * from standard backend response envelope:
 * {
 *   "success": false,
 *   "code": "ZANX-0900",
 *   "message": "Validation failed for one or more fields",
 *   "data": { "password": "Password is required" },
 *   "timestamp": "2026-08-20T11:40:46.9085418"
 * }
 */
export function extractErrorDetails(backendRes: any): { message: string; code?: string; details?: string[] } {
  if (!backendRes) {
    return { message: 'An unexpected error occurred.' };
  }

  if (typeof backendRes === 'string') {
    try {
      backendRes = JSON.parse(backendRes);
    } catch {
      return { message: backendRes };
    }
  }

  const message = backendRes.message || 'Validation failed for one or more fields';
  const code = backendRes.code;
  const details: string[] = [];

  const payloadData = backendRes.data || backendRes.errors || backendRes.fieldErrors || backendRes.details;

  if (payloadData) {
    if (typeof payloadData === 'string') {
      details.push(payloadData);
    } else if (Array.isArray(payloadData)) {
      payloadData.forEach((item: any) => {
        if (typeof item === 'string') details.push(item);
        else if (item && typeof item === 'object') {
          if (item.field && item.message) details.push(`${item.field}: ${item.message}`);
          else if (item.defaultMessage) details.push(item.defaultMessage);
          else details.push(JSON.stringify(item));
        }
      });
    } else if (typeof payloadData === 'object') {
      Object.entries(payloadData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          value.forEach(msg => details.push(`${key}: ${msg}`));
        } else if (typeof value === 'string') {
          details.push(`${key}: ${value}`);
        } else if (value !== null && value !== undefined) {
          details.push(`${key}: ${JSON.stringify(value)}`);
        }
      });
    }
  }

  return { message, code, details: details.length > 0 ? details : undefined };
}

export const httpResponseInterceptor: HttpInterceptorFn = (req, next) => {
  const toastService = inject(ToastService);

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse) {
        let body = event.body as any;
        if (typeof body === 'string') {
          try { body = JSON.parse(body); } catch {}
        }
        if (body && typeof body === 'object' && 'success' in body) {
          if (body.success === true) {
            // For state mutations (POST, PUT, DELETE, PATCH) or if message is present, show success toast
            if (req.method !== 'GET' && body.message) {
              toastService.success(body.message, body.code);
            }
          } else if (body.success === false) {
            // Backend returned 200 OK but success = false envelope
            const { message, code, details } = extractErrorDetails(body);
            if (isInstitutionPending(code, message)) {
              toastService.show(message, 'success', 8000, code, details);
            } else {
              toastService.error(message, code, details);
            }
          }
        }
      }
    }),
    catchError((error: HttpErrorResponse) => {
      let errPayload: any = error.error;
      if (typeof errPayload === 'string') {
        try {
          errPayload = JSON.parse(errPayload);
        } catch {
          // raw non-JSON string
        }
      }

      if (errPayload && typeof errPayload === 'object' && ('message' in errPayload || 'code' in errPayload || 'data' in errPayload || 'success' in errPayload)) {
        const { message, code, details } = extractErrorDetails(errPayload);
        if (isInstitutionPending(code, message)) {
          toastService.show(message, 'success', 8000, code, details);
        } else {
          toastService.error(message, code, details);
        }
      } else if (typeof error.error === 'string') {
        toastService.error(error.error, `HTTP-${error.status}`);
      } else if (error.status === 0) {
        toastService.error('Unable to connect to Zan X-Change backend servers. Please check network connection.', 'NET-ERR');
      } else {
        toastService.error(error.message || 'An unexpected HTTP error occurred', `HTTP-${error.status}`);
      }
      return throwError(() => error);
    })
  );
};

function isInstitutionPending(code?: string, message?: string): boolean {
  if (code === 'ZANX-1011') return true;
  const msg = (message || '').toLowerCase();
  return msg.includes('institution') && (
    msg.includes('approv') ||
    msg.includes('activat') ||
    msg.includes('pending') ||
    msg.includes('not yet')
  );
}
